"use client";

import ChipCard from "@/components/atoms/ChipCard";
import YouTubeVideoOverlay from "@/components/atoms/YouTubeVideoOverlay";
import { LEAD_DROP_VARIANTS } from "@/constants/leadConstants";
import {
  ctaClick,
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
  videoAction,
} from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { RootState } from "@/store/store";
import Image from "next/image";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  LEAD_DROP_FORM_SECTION_VARIANTS,
  LEAD_DROP_FORM_VARIANTS,
  PAGE_TYPES,
} from "../../../constants";
import circlePlay from "../../assets/circle_play.svg";
import dynamic from "next/dynamic";

const RenderLeadForm = dynamic(
  () => import("./components/_lead_form").then((mod) => mod.RenderLeadForm),
  { ssr: false },
);

interface MetaItem {
  id: string | number;
  meta_description: string;
  meta_priority: number;
}

const About1 = ({ data }: { data: any }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showFullContent, setShowFullContent] = useState(false);
  const isMobile = useIsMobile(800);
  const ref = useRef<HTMLElement>(null);
  const dispatch = useDispatch();
  const { data: sectionData } = data;

  const {
    background_color = "#FFFFFF",
    title = "",
    title_text_color = "#000000",
    description = "",
    description_text_color = "#000000",
    desktop_image = "",
    mobile_image = "",
    video_link = "",
    alt_image = "",
    metadata = [],
    metadata_1 = [],
    sub_text = "",
    sub_text_color = "#000000",
    read_more_text = "Read More",
    read_more_color = "#BE2330",
    enable_lead_form,
    mobile_apply_now_button_color,
    mobile_apply_now_icon,
    mobile_apply_now_icon_alt,
    mobile_apply_now_text,
    mobile_apply_now_text_color,
  } = sectionData || {};

  const { pageCategory } = useSelector((state: any) => state.globalslice);

  // Sort metadata by priority using useMemo - MUST be called before early returns
  const sortedMetadata = useMemo(() => {
    if (!metadata || metadata.length === 0) return [];
    return [...metadata].sort(
      (a: MetaItem, b: MetaItem) => a.meta_priority - b.meta_priority,
    );
  }, [metadata]);

  const sortedMetadata1 = useMemo(() => {
    if (!metadata_1 || metadata_1.length === 0) return [];
    return [...metadata_1].sort(
      (a: any, b: any) => (a.priority || 0) - (b.priority || 0),
    );
  }, [metadata_1]);

  const hasDescriptionContent = useMemo(() => {
    if (!description || typeof description !== "string") return false;
    const text = description
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/\s+/g, " ")
      .trim();
    return text.length > 0;
  }, [description]);

  /**
   * True when the image caption box has real content. The CMS rich-text editor stores a
   * cleared field as markup — `<p></p>`, `<p><br></p>`, `&nbsp;` — which is a non-empty
   * string, so a bare `sub_text &&` check rendered an empty bordered box (and reserved its
   * spacing). Most visible on mobile with no image, where the box has nothing to sit on.
   * Same strip-and-measure approach as `hasDescriptionContent` above.
   */
  const hasSubTextContent = useMemo(() => {
    if (!sub_text || typeof sub_text !== "string") return false;
    const text = sub_text
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/\s+/g, " ")
      .trim();
    return text.length > 0;
  }, [sub_text]);

  const descriptionRef = useRef<HTMLDivElement>(null);
  const descriptionColumnRef = useRef<HTMLDivElement>(null);
  const readMoreWidthRetryRef = useRef(0);
  /** Taller than 5 lines at current typography while viewport < sm (640px). */
  const [readMoreNeededNarrow, setReadMoreNeededNarrow] = useState(false);
  /** Taller than 4 lines at current typography while viewport ≥ sm (640px). */
  const [readMoreNeededWide, setReadMoreNeededWide] = useState(false);

  const updateReadMoreLineOverflow = useMemo(() => {
    return () => {
      const el = descriptionRef.current;
      const col = descriptionColumnRef.current;
      if (!el || !col || !hasDescriptionContent || !description) {
        setReadMoreNeededNarrow(false);
        setReadMoreNeededWide(false);
        return;
      }

      const width = col.getBoundingClientRect().width;
      if (width <= 0) {
        if (readMoreWidthRetryRef.current < 10) {
          readMoreWidthRetryRef.current += 1;
          requestAnimationFrame(() => updateReadMoreLineOverflow());
        }
        return;
      }
      readMoreWidthRetryRef.current = 0;

      const cs = getComputedStyle(el);
      let lineHeight = parseFloat(cs.lineHeight);
      if (!Number.isFinite(lineHeight) || cs.lineHeight === "normal") {
        const fs = parseFloat(cs.fontSize);
        lineHeight = Number.isFinite(fs) ? fs * 1.35 : 20;
      }

      const probeLineHeight =
        Number.isFinite(parseFloat(cs.lineHeight)) && cs.lineHeight !== "normal"
          ? cs.lineHeight
          : `${lineHeight}px`;

      const probe = document.createElement("div");
      probe.style.cssText = [
        "position:absolute",
        "left:-99999px",
        "top:0",
        `width:${width}px`,
        "visibility:hidden",
        "pointer-events:none",
        `font-size:${cs.fontSize}`,
        `line-height:${probeLineHeight}`,
        `font-family:${cs.fontFamily}`,
        `font-weight:${cs.fontWeight}`,
        "text-align:justify",
        "word-break:break-word",
      ].join(";");
      probe.innerHTML = description;
      document.body.appendChild(probe);
      const fullHeight = probe.scrollHeight;
      document.body.removeChild(probe);

      const isNarrowViewport = window.matchMedia("(max-width: 639px)").matches;
      const isWideViewport = window.matchMedia("(min-width: 640px)").matches;

      setReadMoreNeededNarrow(
        isNarrowViewport && fullHeight > lineHeight * 5 + 1,
      );
      setReadMoreNeededWide(isWideViewport && fullHeight > lineHeight * 4 + 1);
    };
  }, [description, hasDescriptionContent]);

  useLayoutEffect(() => {
    if (!sectionData?.status || isMobile === null) return;
    updateReadMoreLineOverflow();
  }, [sectionData?.status, isMobile, updateReadMoreLineOverflow]);

  useEffect(() => {
    if (!sectionData?.status || isMobile === null) return;
    const col = descriptionColumnRef.current;
    if (!col || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => updateReadMoreLineOverflow());
    ro.observe(col);
    window.addEventListener("resize", updateReadMoreLineOverflow);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", updateReadMoreLineOverflow);
    };
  }, [sectionData?.status, isMobile, updateReadMoreLineOverflow]);

  // Track widget viewed - MUST be called before early returns
  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "about_section");
  //   }
  // }, []);

  if (!sectionData?.status) {
    return null;
  }

  // Handler functions
  const handleVideoPlayClick = () => {
    if (video_link) {
      videoAction({
        section: "about_section",
        label: video_link,
      });
      setIsOpen(true);
    }
  };

  const isLandingPage =
    PAGE_TYPES.LANDING_HOME.toLocaleLowerCase() ===
      pageCategory.toLocaleLowerCase() ||
    PAGE_TYPES.LANDING_PROGRAM.toLocaleLowerCase() ===
      pageCategory.toLocaleLowerCase();

  const isAuxiliaryPage =
    PAGE_TYPES.AUXILIARY.toLocaleLowerCase() ===
    pageCategory.toLocaleLowerCase();

  /** Below sm: clamp 5 lines (lead) or 4 (!lead); sm+: clamp 4 lines when read-more applies. */
  const mobileDescriptionClampLines =
    !hasDescriptionContent || isAuxiliaryPage || showFullContent
      ? null
      : enable_lead_form && isLandingPage
        ? (5 as const)
        : !enable_lead_form
          ? (4 as const)
          : null;

  const clampSmAndUpDescription =
    Boolean(mobileDescriptionClampLines) && readMoreNeededWide;

  const handleReadMoreClick = () => {
    ctaClick({
      section: "about_section",
      component: "read_more",
      label: read_more_text || "Read More",
      step: "",
      entity: "read_more_button",
    });

    if (isLandingPage && Boolean(enable_lead_form)) {
      setShowFullContent((prev) => !prev);
    } else {
      setIsOpen(true);
    }
  };

  return (
    <>
      <section
        ref={ref}
        style={{ backgroundColor: background_color || "#FFFFFF" }}
        className={cn(
          "box-border flex w-full justify-center",
          "py-5 px-spacing-8 xs:px-5 sm:py-[42px]",
          /* Match page rails (BlogList / section scale): breathable side padding from sm–xl */
          "sm:px-[36px] md:px-[44px] lg:px-[52px] xl:px-[64px]",
        )}
      >
        <div
          className={cn(
            "flex w-full max-w-[1008px]",
            "md:max-w-[1153px] lg:max-w-[1358px] xl:max-w-[1678px]",
            "xs:flex-col items-center max-[799px]:flex-col max-[799px]:items-center",
            hasSubTextContent
              ? "xs:gap-6 max-[799px]:gap-6"
              : "xs:gap-3 max-[799px]:gap-3",
            "sm:gap-6 sm:flex-row sm:items-start",
            "md:gap-[30px] lg:gap-[34px] xl:gap-10",
          )}
        >
          {(!enable_lead_form || isMobile) && (
            <div
              className={cn(
                "flex items-center justify-center shrink-0 cursor-pointer",
                "xs:pb-10 xs:w-full",
                hasSubTextContent ? "max-[799px]:mb-4" : "max-[799px]:mb-1",
                "sm:w-fit sm:pr-6 sm:pb-6",
                "md:pr-6 md:pb-6",
                "lg:pr-[34px] lg:pb-[34px]",
                "xl:pr-[44px] xl:pb-[44px]",
                {
                  "xs:pb-0": enable_lead_form,
                },
              )}
              onClick={handleVideoPlayClick}
            >
              <div
                className={cn(
                  "relative w-full shrink-0 overflow-visible",
                  "sm:w-[388px] md:w-[442px] lg:w-[496px] xl:w-[640px]",
                )}
              >
                {(isMobile ? mobile_image || desktop_image : desktop_image) && (
                  <Image
                    src={
                      isMobile ? mobile_image || desktop_image : desktop_image
                    }
                    width={600}
                    height={600}
                    alt={alt_image}
                    fetchPriority="high"
                    priority
                    className={cn(
                      "block object-cover rounded-lg",
                      "sm:w-[388px] sm:h-[252px]",
                      "md:w-[442px] md:h-[286px]",
                      "lg:w-[496px] lg:h-[324px]",
                      "xl:w-[640px] xl:h-[404px]",
                      {
                        "xs:w-full xs:h-[214px]": !enable_lead_form,
                        "xs:w-full xs:h-[180px]": enable_lead_form,
                      },
                    )}
                  />
                )}

                {hasSubTextContent && (
                  <div
                    className={cn(
                      "absolute bottom-0 right-0 z-10 w-[50%] origin-bottom-right bg-white",
                      "rounded-tl-lg rounded-br-lg",
                      "pl-3 pt-3",
                      "max-[799px]:box-border max-[799px]:h-[73px] max-[799px]:w-[58%] max-[799px]:overflow-hidden max-[799px]:pl-2 max-[799px]:pt-2 max-[799px]:pb-0",
                      "max-[799px]:translate-y-[32%]",
                      "min-[800px]:translate-y-3 min-[800px]:overflow-visible",
                    )}
                  >
                    <div
                      className={cn(
                        "overflow-hidden border border-secondary-3 p-2",
                        "rounded-tl-lg rounded-br-lg",
                        "text-[12px] sm:text-[14px] md:text-[16px] lg:text-[18px] xl:text-[24px]",
                        "max-[799px]:box-border max-[799px]:h-[65px] max-[799px]:min-h-[65px] max-[799px]:max-h-[65px] max-[799px]:shrink-0 max-[799px]:rounded-md max-[799px]:leading-snug max-[799px]:line-clamp-3",
                        "max-[799px]:[&_*]:m-0",
                        "min-[800px]:h-auto min-[800px]:line-clamp-3",
                        "min-[800px]:min-h-[63px] lg:min-h-[66px] xl:min-h-[92px]",
                      )}
                      suppressHydrationWarning={true}
                      dangerouslySetInnerHTML={{ __html: sub_text }}
                    />
                  </div>
                )}

                {circlePlay && video_link && (
                  <Image
                    src={circlePlay}
                    alt="circle-play"
                    className={cn(
                      "pointer-events-none absolute top-1/2 left-1/2 z-20 -translate-x-1/2 -translate-y-1/2",
                      "xs:size-11",
                    )}
                  />
                )}
              </div>
            </div>
          )}

          <div
            className={cn(
              "flex w-full flex-col sm:min-w-0 sm:flex-1",
              "xs:gap-1",
              /* Tablet sm–1079: unchanged (sm:gap-2). Title → description from 1080+: 24 / 32 / 40 / 48 */
              "sm:gap-2 min-[1080px]:gap-[24px] min-[1230px]:gap-[32px] min-[1430px]:gap-[40px] min-[1750px]:gap-[48px]",
            )}
          >
            <h2
              className={cn(
                "line-clamp-2 sm:line-clamp-1",
                "xs:text-center xs:text-new-body-24-semibold",
                "sm:text-left sm:text-heading-5-semibold",
                "md:text-heading-4-semibold",
                "lg:text-semibold lg:text-[34px]",
                "xl:text-semibold xl:text-[42px]",
                "type-h3",
              )}
              style={{ color: title_text_color }}
            >
              {title}
            </h2>
            <div
              className={cn(
                "flex w-full flex-col items-start",
                "xs:gap-3",
                "sm:gap-6",
              )}
            >
              <div
                ref={descriptionColumnRef}
                className={cn(
                  "flex w-full flex-col items-start",
                  "xs:gap-3 xs:text-center",
                  "sm:gap-[10px] sm:text-left",
                  { "sm:gap-8": enable_lead_form },
                  /* From 1080px: spec spacing via quote block margin; keep mobile/tablet flex gaps above */
                  "min-[1080px]:gap-0",
                )}
              >
                <div>
                  <div
                    ref={descriptionRef}
                    className={cn(
                      "text-justify max-sm:text-start",
                      "xs:text-heading-9-regular",
                      "sm:text-heading-9-regular sm:text-[12px]",
                      "md:text-heading-8-regular md:text-[14px]",
                      "lg:text-body-1-regular lg:text-[16px]",
                      "xl:text-new-body-24-regular xl:text-[22px]",
                      mobileDescriptionClampLines === 5 &&
                        readMoreNeededNarrow &&
                        "",
                      mobileDescriptionClampLines === 4 &&
                        readMoreNeededNarrow &&
                        "max-sm:line-clamp-4",
                      clampSmAndUpDescription &&
                        "sm:text-start",
                      "type-body-sm",
                    )}
                    style={{ color: description_text_color }}
                    suppressHydrationWarning={true}
                    dangerouslySetInnerHTML={{ __html: description || "" }}
                  />

                  {!isAuxiliaryPage &&
                    isLandingPage &&
                    read_more_text &&
                    hasDescriptionContent &&
                    enable_lead_form &&
                    (readMoreNeededNarrow || readMoreNeededWide) && (
                      <div
                        className={cn(
                          "cursor-pointer underline xs:text-heading-8-semibold",
                          readMoreNeededNarrow
                            ? "max-sm:block"
                            : "max-sm:hidden",
                          readMoreNeededWide ? "sm:block" : "sm:hidden",
                          "type-h6",
                        )}
                        style={{ color: read_more_color }}
                        onClick={handleReadMoreClick}
                      >
                        {showFullContent ? "Show Less" : read_more_text}
                      </div>
                    )}
                  {!enable_lead_form &&
                    !isAuxiliaryPage &&
                    read_more_text &&
                    hasDescriptionContent &&
                    (readMoreNeededNarrow || readMoreNeededWide) && (
                      <div
                        className={cn(
                          "cursor-pointer underline xs:text-heading-8-semibold",
                          readMoreNeededNarrow
                            ? "max-sm:block"
                            : "max-sm:hidden",
                          readMoreNeededWide ? "sm:block" : "sm:hidden",
                          "type-h6",
                        )}
                        style={{ color: read_more_color }}
                        onClick={() => setShowFullContent((prev) => !prev)}
                      >
                        {showFullContent ? "Show Less" : read_more_text}
                      </div>
                    )}
                </div>
                <div
                  className={cn(
                    "flex flex-col gap-2",
                    /* Description → quote: 16 / 20 / 24 / 28 @ 1080+ only (mobile/tablet use flex gap above) */
                    "min-[1080px]:mt-[16px] min-[1230px]:mt-[20px] min-[1430px]:mt-[24px] min-[1750px]:mt-[28px]",
                  )}
                >
                  {sortedMetadata.map((item: any) => (
                    <div
                      key={item.id}
                      className={cn(
                        "border-secondary-main",
                        "xs:border-l-3 xs:rounded-tl-lg xs:rounded-bl-lg xs:border-t-0 xs:px-5 xs:text-heading-9-semibold",
                        "sm:pl-[14px] sm:text-heading-9-semibold sm:border-l-3 sm:rounded-tl-lg sm:rounded-bl-lg sm:border-t-0",
                        "md:text-heading-8-semibold",
                        "lg:text-body-1-semibold",
                        "xl:text-new-body-24-semibold",
                        "line-clamp-4 sm:line-clamp-2",
                        "type-h6",
                      )}
                      suppressHydrationWarning={true}
                      dangerouslySetInnerHTML={{
                        __html: item.meta_description,
                      }}
                    ></div>
                  ))}
                </div>

                {/* {!Boolean(enable_lead_form) && (
                <div className={cn("w-full flex", "xs:justify-center")}>
                  <Button
                    classType="outline"
                    withArrow={true}
                    className={cn(
                      "w-fit text-body-2-semibold border-0",
                      "xs:flex",
                      "sm:hidden"
                    )}
                    arrowFill={read_more_color}
                    onClick={handleReadMoreClick}
                  >
                    <span style={{ color: read_more_color }}>
                      {read_more_text || "Know More"}
                    </span>
                  </Button>
                </div>
              )} */}

                {Boolean(enable_lead_form) && sortedMetadata1.length > 0 && (
                  <div
                    className={cn(
                      // Full row width + centered chips (parents use items-start; xs: starts at 310px only)
                      "flex w-full min-w-0 self-stretch flex-wrap justify-center gap-3 xs:pt-9",
                      "sm:flex-nowrap sm:justify-start sm:gap-[26px]",
                    )}
                  >
                    {sortedMetadata1.map((item) => (
                      <ChipCard key={item.id} {...item} />
                    ))}
                  </div>
                )}
                {Boolean(enable_lead_form) &&
                  isMobile &&
                  mobile_apply_now_text && (
                    <div
                      className={cn(
                        "w-full flex",
                        " xs:pt-6 xs:justify-center",
                      )}
                    >
                      <div
                        style={{
                          backgroundColor: mobile_apply_now_button_color,
                        }}
                        className={cn(
                          "flex items-center justify-center rounded-md gap-2",
                          "xs:px-[10px] xs:py-[6px] xs:text-heading-7-semibold",
                          "type-h6",
                        )}
                        onClick={() =>
                          dispatch(
                            handleOpenLeaddrop({
                              variant: LEAD_DROP_VARIANTS.REGISTER,
                            }),
                          )
                        }
                      >
                        <span style={{ color: mobile_apply_now_text_color }}>
                          {mobile_apply_now_text}
                        </span>
                        {mobile_apply_now_icon && (
                          <Image
                            src={mobile_apply_now_icon}
                            alt={mobile_apply_now_icon_alt || ""}
                            width={20}
                            height={20}
                          />
                        )}
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </div>
          <RenderLeadForm enable_lead_form={enable_lead_form} />
        </div>
      </section>
      {video_link && (
        <YouTubeVideoOverlay
          isOpen={isOpen}
          onClose={() => {
            setIsOpen(false);
          }}
          videoUrl={video_link}
        />
      )}
    </>
  );
};

export default About1;
